import React, { useEffect, useState } from 'react';
import { apiGet, apiDownload } from '../../../lib/api';
import { GraduationCap, CalendarClock, Award, FileText, AlertTriangle } from 'lucide-react';
import { formatDate } from '../../../lib/format';

const STATUS_STYLE = {
  valid: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  expiring: 'bg-amber-50 text-amber-700 border-amber-200',
  expired: 'bg-red-50 text-red-600 border-red-200',
  no_expiry: 'bg-slate-100 text-slate-500 border-slate-300',
};

export default function MyTraining() {
  const [data, setData] = useState(null);

  useEffect(() => { apiGet('/hr/me/training-history').then(setData).catch(() => setData({ linked: false, records: [], upcoming: [] })); }, []);

  if (!data) return <div className="p-8 text-slate-500">Loading…</div>;

  const expiringSoon = (data.records || []).filter((r) => r.status === 'expiring' || r.status === 'expired');

  return (
    <div className="space-y-5 p-6 md:p-8 max-w-4xl" data-testid="my-training-page">
      <div>
        <div className="text-[10px] font-display font-bold uppercase tracking-[0.2em] text-[#1F7A6E] mb-2">My Workspace</div>
        <h1 className="font-display text-3xl font-extrabold tracking-tight">My Training</h1>
        <p className="text-sm text-slate-600 mt-1">Your certifications, scores and upcoming sessions.</p>
      </div>

      {data.linked === false && (
        <div className="bg-amber-50 border border-amber-200 rounded-sm p-4 text-sm text-amber-800" data-testid="no-emp-linked">
          No employee record is linked to your login yet. HR will link it shortly.
        </div>
      )}

      {expiringSoon.length > 0 && (
        <div className="bg-red-50 border border-red-200 rounded-sm p-4" data-testid="my-expiry-alert">
          <div className="flex items-center gap-2 text-sm font-display font-bold text-red-700"><AlertTriangle size={16} /> Action needed</div>
          <ul className="mt-2 text-xs text-red-700 space-y-1">
            {expiringSoon.map((r) => (
              <li key={r.id}>{r.plan_name} — {r.status === 'expired' ? <b>expired {r.expiry_date}</b> : <>expires <b>{r.expiry_date}</b></>}</li>
            ))}
          </ul>
        </div>
      )}

      {/* Upcoming */}
      <div className="bg-white border border-slate-200 rounded-sm">
        <div className="px-4 py-3 border-b"><h3 className="text-xs font-display font-bold uppercase tracking-wider text-slate-500 flex items-center gap-2"><CalendarClock size={14} /> Upcoming Sessions</h3></div>
        {(data.upcoming || []).length === 0 ? <div className="p-4 text-xs text-slate-400">No upcoming sessions.</div> : (
          <div className="divide-y divide-slate-100">
            {data.upcoming.map((s, i) => (
              <div key={i} className="p-4" data-testid={`my-upcoming-${i}`}>
                <div className="font-display font-bold text-sm">{s.title}</div>
                <div className="text-[11px] text-slate-500">{s.plan_name} · {s.scheduled_at ? formatDate(s.scheduled_at) : 'TBD'}{s.location ? ` · ${s.location}` : ''}{s.trainer_name ? ` · ${s.trainer_name}` : ''}</div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Certifications */}
      <div className="bg-white border border-slate-200 rounded-sm">
        <div className="px-4 py-3 border-b"><h3 className="text-xs font-display font-bold uppercase tracking-wider text-slate-500 flex items-center gap-2"><Award size={14} /> My Certifications ({(data.records || []).length})</h3></div>
        {(data.records || []).length === 0 ? <div className="p-4 text-xs text-slate-400">No completed training yet.</div> : (
          <div className="divide-y divide-slate-100">
            {data.records.map((r) => (
              <div key={r.id} className="p-4 flex flex-wrap items-center justify-between gap-2" data-testid={`my-record-${r.id}`}>
                <div>
                  <div className="font-display font-bold text-sm flex items-center gap-2"><GraduationCap size={14} className="text-[#1F7A6E]" /> {r.plan_name}</div>
                  <div className="text-[11px] text-slate-500 mt-0.5">{r.session_title} · {r.category || ''}{r.score != null ? ` · Score ${r.score}` : ''} · Completed {r.completed_at ? formatDate(r.completed_at) : '—'}</div>
                </div>
                <div className="flex items-center gap-2">
                  <span className={`text-[9px] font-bold uppercase px-2 py-0.5 rounded-sm border ${STATUS_STYLE[r.status] || STATUS_STYLE.no_expiry}`}>{r.status === 'no_expiry' ? 'No expiry' : r.status === 'valid' ? `Valid · ${r.expiry_date}` : r.status === 'expired' ? `Expired ${r.expiry_date}` : `Expires ${r.expiry_date}`}</span>
                  {r.certificate_url && <button onClick={() => apiDownload(`/hr/training/records/${r.id}/certificate`, `cert_${r.plan_name}`)} data-testid={`my-cert-${r.id}`} className="text-[#1F7A6E] flex items-center gap-1 text-xs font-bold"><FileText size={12} /> Certificate</button>}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
